// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import mapKeys from 'lodash/mapKeys';
import zipWith from 'lodash/zipWith';
import sitePreferencesConfig from '../../../../../config/sitePreferencesConfig.json';
import { pickByKeyPrefix } from '../../common/utils/objectUtils';
import { sortArrayOfNumbers } from '../../common/utils/arrayUtils';
import getFrontConfig from '../../common/getFrontConfig';

const FRONT_CONFIG = getFrontConfig();

/**
 * Transform the ProductImages model of SFRA (a type-based map of arrays of
 * images) to an unique array of objects that group all the types of a single
 * image.
 *
 * From:
 * {
 *   ...
 *   "medium": ProductImage[],
 *   "plp.200": ProductImage[],
 *   ...
 * }
 *
 * We get:
 * [
 *   {
 *     ...
 *     "medium": ProductImage,
 *     "plp.200": ProductImage,
 *     ...
 *   },
 *   {
 *     ...
 *     "medium": ProductImage,
 *     "plp.200": ProductImage,
 *     ...
 *   },
 *   ...
 * ]
 *
 * @param {ProductImages} productImages
 * @returns {ProductImageSet[]}
 */
function createProductImageSetArrayFromProductImages(productImages) {
  let result = [];
  if (productImages) {
    const types = Object.keys(productImages);
    const imageArrays = Object.values(productImages);
    result = zipWith(...imageArrays, (...images) =>
      // build an object of types an corresponding image
      images.reduce((res, image, index) => {
        const typeId = types[index];
        res[typeId] = image;
        return res;
      }, {})
    );
  }
  return result;
}

export const productImageSetMixin = {
  computed: {
    /**
     * @type {ProductImageSet[]} An unique array of objects which group all the
     * different types of a single image.
     */
    productImageFullSetArray() {
      return createProductImageSetArrayFromProductImages(this.productImages);
    },
  },

  methods: {
    /**
     * Generate a valid ProductImage array from a product custom image.
     * @param {Object} product
     * @returns {Array}
     */
    createCustomImageObject(product) {
      return [
        {
          alt: product.sliderProductName || product.productName,
          url: product.image,
          title: product.sliderProductName || product.productName,
          absURL: product.image,
        },
      ];
    },

    /**
     * Get the default image (usually the smallest one) from a image set.
     * @param {ProductImageSet} productImageSet
     * @returns {ProductImage|Object}
     */
    getDefaultImageFromProductImageSet(productImageSet) {
      if (!productImageSet) return {};
      const types = Object.keys(productImageSet);
      const lowestWidth = sortArrayOfNumbers(types).shift();
      const defaultImageKey = Number.isNaN(lowestWidth)
        ? types[0]
        : `${lowestWidth}`;
      return productImageSet[defaultImageKey];
    },

    /**
     * Get the placeholder image from a image set. Returns null if no placeholder
     * is defined.
     * @param {ProductImageSet} productImageSet
     * @returns {ProductImage}
     */
    getPlaceholderImageFromProductImageSet(productImageSet) {
      if (
        FRONT_CONFIG.productImages.placeholderType !==
        sitePreferencesConfig.customAttributes.imagePlaceholderType.values
          .blurryImage
      ) {
        return null;
      }
      return productImageSet.placeholder ?? null;
    },

    /**
     * Creates an array of image sets related by the image type prefix.
     *
     * From:
     * {
     *   ...
     *   "medium": ProductImage[],
     *   "plp.200": ProductImage[],
     *   "plp.400": ProductImage[],
     *   "plp.600": ProductImage[],
     *   "plp.800": ProductImage[],
     *   "plp.1200": ProductImage[],
     *   ...
     * }
     *
     * We get this for a set named "plp":
     * [
     *   {
     *     "200": ProductImage,
     *     "400": ProductImage,
     *     "600": ProductImage,
     *     "800": ProductImage,
     *     "1200": ProductImage,
     *   },...
     * ]
     *
     * @param {string} [imageSetName='plp']
     * @returns {ProductImageSet[]}
     */
    getProductImageSets(
      imageSetName = FRONT_CONFIG.productImages.imageSets.PLP,
      customImages = null
    ) {
      const imageSetTypePrefix = `${imageSetName}.`;
      const filteredProductImages = pickByKeyPrefix(
        customImages || this.productImages,
        imageSetTypePrefix
      );
      const imageArraysByImageSetTypes = mapKeys(
        filteredProductImages,
        (_, key) => key.substr(imageSetTypePrefix.length)
      );
      return createProductImageSetArrayFromProductImages(
        imageArraysByImageSetTypes
      );
    },

    /**
     * Creates the value of srcset property for the given image set.
     * ProductImageSet keys have to be widths.
     * @param {ProductImageSet} imageSet
     * @returns {string}
     */
    getSrcsetFromProductImageSet(productImageSet) {
      if (!productImageSet) return '';
      return Object.entries(productImageSet)
        .map(([type, image]) => {
          const width = +type; // Convert to number
          if (Number.isNaN(width)) return null;
          if (!image?.url) return null;
          return `${image.url} ${width}w`;
        })
        .filter((identity) => identity) // Remove null entries
        .join(', ');
    },
  },
};

export default productImageSetMixin;
